import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Trans, useTranslation } from 'react-i18next';
import { ShieldCheck, Loader2, AlertCircle, CheckCircle2 } from 'lucide-react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';

// OAuth endpoints live at /oauth/*, NOT under /api/oauth/* like the rest
// of the BusyBee API - RFC 6749 / OAuth 2.1 / RFC 8414 all expect bare
// /authorize, /token, /register paths so MCP clients can discover them
// from the canonical AS metadata document. The shared `api` axios
// instance prepends /api, so we use a small dedicated client here.
const BACKEND_URL = (process.env.REACT_APP_BACKEND_URL || '').replace(/\/$/, '');
const oauthApi = axios.create({ baseURL: BACKEND_URL });
oauthApi.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Maps the raw scope strings stored in the OAuth client to a human label
// shown on the consent screen. Anything not in this map is shown verbatim.
const SCOPE_LABELS = {
  'mcp:read': 'oauth_consent.scope_read',
  'mcp:write': 'oauth_consent.scope_write',
  'mcp:admin': 'oauth_consent.scope_admin',
};

/**
 * Render the OAuth 2.1 consent step for MCP clients (Claude Desktop /
 * Claude.ai / Cursor / ChatGPT / …). Reached via the backend's
 * /oauth/authorize endpoint, which 302s the browser here with all the
 * OAuth query parameters preserved.
 *
 * The page:
 *   1. If the user isn't logged in to BusyBee, redirect to /login with a
 *      `next` parameter so they come back here after sign-in.
 *   2. Look up the requesting client by client_id so we can show its name.
 *   3. Render an Allow / Deny screen.
 *   4. POST the decision to /oauth/authorize/decision and follow the
 *      backend-supplied redirect_to URL (always the client's registered
 *      redirect_uri - never anything else).
 */
const OAuthAuthorizePage = () => {
  const { t } = useTranslation();
  const { user, isAuthenticated, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const params = new URLSearchParams(location.search);
  const clientId = params.get('client_id') || '';
  const redirectUri = params.get('redirect_uri') || '';
  const codeChallenge = params.get('code_challenge') || '';
  const codeChallengeMethod = params.get('code_challenge_method') || 'S256';
  const requestedScope = params.get('scope') || '';
  const state = params.get('state') || '';
  const resource = params.get('resource') || '';

  const [client, setClient] = useState(null);
  const [error, setError] = useState('');
  const [loadingClient, setLoadingClient] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  // Step 1 - bounce to login if not authenticated.
  useEffect(() => {
    if (authLoading) return;
    if (!isAuthenticated) {
      const redirect = encodeURIComponent(location.pathname + location.search);
      navigate(`/login?redirect=${redirect}`, { replace: true });
    }
  }, [authLoading, isAuthenticated, location, navigate]);

  // Step 2 - load client metadata so we can show its name.
  useEffect(() => {
    if (!clientId) {
      setError(t('oauth_consent.missing_client'));
      setLoadingClient(false);
      return;
    }
    let cancelled = false;
    oauthApi
      .get(`/oauth/register/${clientId}`)
      .then((r) => {
        if (!cancelled) setClient(r.data);
      })
      .catch(() => {
        if (!cancelled) setError(t('oauth_consent.unknown_client'));
      })
      .finally(() => {
        if (!cancelled) setLoadingClient(false);
      });
    return () => {
      cancelled = true;
    };
  }, [clientId, t]);

  const submit = async (decision) => {
    setSubmitting(true);
    setError('');
    try {
      const res = await oauthApi.post('/oauth/authorize/decision', {
        client_id: clientId,
        redirect_uri: redirectUri,
        code_challenge: codeChallenge,
        code_challenge_method: codeChallengeMethod,
        scope: requestedScope || (client && client.scope) || 'mcp:read mcp:write',
        state: state || undefined,
        resource: resource || undefined,
        decision,
      });
      // The backend computed the exact URL to redirect to (either
      // <redirect_uri>?code=... or <redirect_uri>?error=...). Honour it.
      window.location.href = res.data.redirect_to;
    } catch (e) {
      const msg = e?.response?.data?.detail || t('oauth_consent.failed');
      setError(typeof msg === 'string' ? msg : JSON.stringify(msg));
      setSubmitting(false);
    }
  };

  if (authLoading || loadingClient) {
    return (
      <div className="min-h-screen bg-[#0a0a0b] flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-amber-400 animate-spin" />
      </div>
    );
  }

  // Reduce the requested scope to the intersection with what the client
  // actually registered for, so we don't promise more than we'll grant.
  const grantedScopes = (() => {
    const clientScope = (client && client.scope) || 'mcp:read mcp:write';
    const wanted = (requestedScope || clientScope).split(/\s+/).filter(Boolean);
    const allowed = clientScope.split(/\s+/).filter(Boolean);
    return wanted.filter((s) => allowed.includes(s));
  })();

  const clientName = (client && (client.client_name || client.id)) || clientId;

  return (
    <div className="min-h-screen bg-[#0a0a0b] flex items-center justify-center px-4">
      <div className="w-full max-w-lg bg-[#1a1a1b] rounded-2xl border border-gray-800 p-8 shadow-2xl">
        <div className="flex items-center gap-3 mb-6">
          <div className="p-3 rounded-xl bg-amber-500/10 border border-amber-500/30">
            <ShieldCheck className="w-6 h-6 text-amber-400" />
          </div>
          <div>
            <h1 className="text-xl font-bold text-white">{t('oauth_consent.title')}</h1>
            <p className="text-xs text-gray-500">
              <Trans t={t} i18nKey="oauth_consent.signed_in_as" values={{ email: user?.email }}
                components={{ b: <span className="text-gray-300" /> }} />
            </p>
          </div>
        </div>

        {error ? (
          <div className="flex items-start gap-2 mb-6 p-3 rounded-lg bg-red-500/10 border border-red-500/30 text-sm text-red-300">
            <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" />
            <span>{error}</span>
          </div>
        ) : null}

        <p className="text-gray-300 mb-4">
          <Trans t={t} i18nKey="oauth_consent.wants" values={{ client: clientName }}
            components={{ b: <strong className="text-amber-400" /> }} />
        </p>

        <div className="rounded-lg border border-gray-800 bg-black/30 p-4 mb-6">
          <p className="text-xs uppercase tracking-wider text-gray-500 mb-3">
            {t('oauth_consent.able')}
          </p>
          <ul className="space-y-2">
            {grantedScopes.length === 0 ? (
              <li className="text-sm text-gray-400 italic">
                {t('oauth_consent.no_scopes')}
              </li>
            ) : (
              grantedScopes.map((scope) => (
                <li key={scope} className="flex items-start gap-2 text-sm text-gray-200">
                  <CheckCircle2 className="w-4 h-4 mt-0.5 text-amber-400 shrink-0" />
                  <span>
                    {SCOPE_LABELS[scope] ? t(SCOPE_LABELS[scope]) : scope}
                  </span>
                </li>
              ))
            )}
          </ul>
        </div>

        <p className="text-xs text-gray-500 mb-6">
          <Trans t={t} i18nKey="oauth_consent.revoke"
            components={{ a: <a href="/app/settings?tab=api" className="text-amber-400 hover:underline" /> }} />
        </p>

        <div className="flex gap-3">
          <button
            type="button"
            disabled={submitting || !!error}
            onClick={() => submit('allow')}
            className="flex-1 bg-amber-500 hover:bg-amber-400 disabled:opacity-50 disabled:cursor-not-allowed text-black font-medium py-2.5 rounded-lg transition-colors"
          >
            {submitting ? t('oauth_consent.connecting') : t('oauth_consent.allow')}
          </button>
          <button
            type="button"
            disabled={submitting}
            onClick={() => submit('deny')}
            className="flex-1 border border-gray-700 hover:bg-white/5 disabled:opacity-50 text-white font-medium py-2.5 rounded-lg transition-colors"
          >
            {t('oauth_consent.deny')}
          </button>
        </div>
      </div>
    </div>
  );
};

export default OAuthAuthorizePage;
